import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Table,
  Button,
  Typography,
  Card,
  Spin,
  Modal,
  Input,
  Select,
  Tag,
  Space,
  message,
  Popconfirm,
  Empty,
  TimePicker,
} from 'antd';
import {
  PlusOutlined,
  ShopOutlined,
  DeleteOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  SearchOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import {
  getAllBooths,
  getVendors,
  createBooth,
  deleteBooth,
  addVendorToBooth,
  removeVendorFromBooth,
  getBoothScheduleSlots,
  createScheduleSlot,
  updateScheduleSlot,
  deleteScheduleSlot,
  Booth,
  Vendor,
} from '@festpix/services';
import type { ScheduleSlot, ScheduleSlotUser } from '@festpix/types';
import { getAllUsers, User } from '../../services/adminUserService';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;

export default function BoothsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booths, setBooths] = useState<Booth[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [vendorModalVisible, setVendorModalVisible] = useState(false);
  const [selectedBooth, setSelectedBooth] = useState<Booth | null>(null);
  const [newBoothName, setNewBoothName] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [addingVendor, setAddingVendor] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Schedule state
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [scheduleSlots, setScheduleSlots] = useState<ScheduleSlot[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [savingSlot, setSavingSlot] = useState(false);
  const [editingSlot, setEditingSlot] = useState<ScheduleSlot | null>(null);
  const [slotStartTime, setSlotStartTime] = useState<dayjs.Dayjs | null>(null);
  const [slotEndTime, setSlotEndTime] = useState<dayjs.Dayjs | null>(null);
  const [slotUserIds, setSlotUserIds] = useState<string[]>([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadData();
      }
    });
    return unsubscribe;
  }, [navigate]);

  const loadData = async () => {
    try {
      const [boothsData, vendorsData] = await Promise.all([
        getAllBooths(),
        getVendors(),
      ]);
      setBooths(boothsData);
      setVendors(vendorsData);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooth = async () => {
    if (!newBoothName.trim()) {
      message.error('Por favor, insira o nome do estande');
      return;
    }

    setCreating(true);
    try {
      const newBooth = await createBooth({ name: newBoothName.trim() });
      setBooths([...booths, newBooth]);
      setCreateModalVisible(false);
      setNewBoothName('');
      message.success('Estande criado com sucesso!');
    } catch (error: any) {
      message.error(error.message || 'Erro ao criar estande');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBooth = async (boothId: string) => {
    try {
      await deleteBooth(boothId);
      setBooths(booths.filter((b) => b.id !== boothId));
      message.success('Estande excluído com sucesso!');
    } catch (error: any) {
      message.error(error.message || 'Erro ao excluir estande');
    }
  };

  const handleOpenVendorModal = (booth: Booth) => {
    setSelectedBooth(booth);
    setSelectedVendorId('');
    setVendorModalVisible(true);
  };

  const handleAddVendor = async () => {
    if (!selectedBooth || !selectedVendorId) {
      message.error('Por favor, selecione um vendedor');
      return;
    }

    setAddingVendor(true);
    try {
      await addVendorToBooth(selectedBooth.id, selectedVendorId);
      // Update local state
      setBooths(
        booths.map((b) =>
          b.id === selectedBooth.id
            ? { ...b, vendorIds: [...b.vendorIds, selectedVendorId] }
            : b
        )
      );
      setSelectedBooth({
        ...selectedBooth,
        vendorIds: [...selectedBooth.vendorIds, selectedVendorId],
      });
      setSelectedVendorId('');
      message.success('Vendedor adicionado com sucesso!');
    } catch (error: any) {
      message.error(error.message || 'Erro ao adicionar vendedor');
    } finally {
      setAddingVendor(false);
    }
  };

  const handleRemoveVendor = async (vendorId: string) => {
    if (!selectedBooth) return;

    try {
      await removeVendorFromBooth(selectedBooth.id, vendorId);
      // Update local state
      const updatedVendorIds = selectedBooth.vendorIds.filter((id: string) => id !== vendorId);
      setBooths(
        booths.map((b) =>
          b.id === selectedBooth.id ? { ...b, vendorIds: updatedVendorIds } : b
        )
      );
      setSelectedBooth({ ...selectedBooth, vendorIds: updatedVendorIds });
      message.success('Vendedor removido com sucesso!');
    } catch (error: any) {
      message.error(error.message || 'Erro ao remover vendedor');
    }
  };

  const getVendorName = (vendorId: string) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    return vendor ? `${vendor.name} (${vendor.email})` : vendorId;
  };

  const getAvailableVendors = () => {
    if (!selectedBooth) return vendors;
    return vendors.filter((v) => !selectedBooth.vendorIds.includes(v.id));
  };

  // Schedule handlers
  const handleOpenScheduleModal = async (booth: Booth) => {
    setSelectedBooth(booth);
    setScheduleModalVisible(true);
    setLoadingSchedule(true);
    resetSlotForm();
    try {
      const [slotsData, usersData] = await Promise.all([
        getBoothScheduleSlots(booth.id),
        getAllUsers(),
      ]);
      setScheduleSlots(slotsData);
      setAllUsers(usersData);
    } catch (error) {
      console.error('Error loading schedule:', error);
      message.error('Erro ao carregar escalas');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const resetSlotForm = () => {
    setSlotStartTime(null);
    setSlotEndTime(null);
    setSlotUserIds([]);
    setEditingSlot(null);
  };

  const handleSaveSlot = async () => {
    if (!selectedBooth || !slotStartTime || !slotEndTime) {
      message.error('Preencha os horários de início e término');
      return;
    }
    if (slotUserIds.length === 0) {
      message.error('Selecione pelo menos um usuário');
      return;
    }

    const startTime = slotStartTime.format('HH:mm');
    const endTime = slotEndTime.format('HH:mm');
    const assignedUsers: ScheduleSlotUser[] = slotUserIds.map((uid) => {
      const user = allUsers.find((u) => u.id === uid);
      return { userId: uid, userName: user?.name || uid };
    });

    setSavingSlot(true);
    try {
      if (editingSlot) {
        await updateScheduleSlot(editingSlot.id, { startTime, endTime, assignedUsers });
        setScheduleSlots(
          scheduleSlots
            .map((s) => (s.id === editingSlot.id ? { ...s, startTime, endTime, assignedUsers } : s))
            .sort((a, b) => a.startTime.localeCompare(b.startTime))
        );
        message.success('Escala atualizada com sucesso!');
      } else {
        const newSlot = await createScheduleSlot(selectedBooth.id, { startTime, endTime, assignedUsers });
        setScheduleSlots(
          [...scheduleSlots, newSlot].sort((a, b) => a.startTime.localeCompare(b.startTime))
        );
        message.success('Escala criada com sucesso!');
      }
      resetSlotForm();
    } catch (error: any) {
      message.error(error.message || 'Erro ao salvar escala');
    } finally {
      setSavingSlot(false);
    }
  };

  const handleEditSlot = (slot: ScheduleSlot) => {
    setEditingSlot(slot);
    setSlotStartTime(dayjs(slot.startTime, 'HH:mm'));
    setSlotEndTime(dayjs(slot.endTime, 'HH:mm'));
    setSlotUserIds(slot.assignedUsers.map((u) => u.userId));
  };

  const handleDeleteSlot = async (slotId: string) => {
    try {
      await deleteScheduleSlot(slotId);
      setScheduleSlots(scheduleSlots.filter((s) => s.id !== slotId));
      message.success('Escala excluída com sucesso!');
    } catch (error: any) {
      message.error(error.message || 'Erro ao excluir escala');
    }
  };

  const filteredBooths = useMemo(() => {
    if (!searchText.trim()) return booths;
    const search = searchText.toLowerCase();
    return booths.filter((b) => {
      const vendorNames = b.vendorIds
        .map((id: string) => vendors.find((v) => v.id === id)?.name || '')
        .join(' ')
        .toLowerCase();
      return (
        b.name?.toLowerCase().includes(search) ||
        b.id.toLowerCase().includes(search) ||
        b.mainWalletId?.toLowerCase().includes(search) ||
        vendorNames.includes(search)
      );
    });
  }, [booths, vendors, searchText]);

  const columns: ColumnsType<Booth> = [
    {
      title: 'Nome do Estande',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Saldo da Carteira',
      dataIndex: 'walletBalance',
      key: 'walletBalance',
      render: (balance) => (
        <Text style={{ color: colors.success, fontWeight: 500 }}>
          R$ {(balance || 0).toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => (a.walletBalance || 0) - (b.walletBalance || 0),
    },
    {
      title: 'Vendedores',
      dataIndex: 'vendorIds',
      key: 'vendorIds',
      render: (vendorIds: string[]) => (
        <Space wrap>
          {vendorIds.length === 0 ? (
            <Text type="secondary">Nenhum vendedor</Text>
          ) : (
            vendorIds.map((id) => {
              const vendor = vendors.find((v) => v.id === id);
              return (
                <Tag key={id} color={colors.info}>
                  {vendor?.name || id.substring(0, 8)}
                </Tag>
              );
            })
          )}
        </Space>
      ),
    },
    {
      title: 'ID da Carteira',
      dataIndex: 'mainWalletId',
      key: 'mainWalletId',
      render: (id) => (
        <Text code style={{ fontSize: 12 }}>
          {id?.substring(0, 12)}...
        </Text>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<ShoppingOutlined />}
            onClick={() => navigate(`/admin/booths/${record.id}/skus`)}
            style={{ background: colors.success }}
          >
            SKUs
          </Button>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => handleOpenVendorModal(record)}
            style={{ background: colors.info }}
          >
            Vendedores
          </Button>
          <Button
            type="primary"
            icon={<ClockCircleOutlined />}
            onClick={() => handleOpenScheduleModal(record)}
            style={{ background: colors.warning }}
          >
            Escalas
          </Button>
          <Popconfirm
            title="Excluir estande"
            description="Tem certeza que deseja excluir este estande?"
            onConfirm={() => handleDeleteBooth(record.id)}
            okText="Sim"
            cancelText="Não"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Excluir
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <ShopOutlined style={{ marginRight: 12 }} />
          Estandes
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => setCreateModalVisible(true)}
          style={{ background: colors.success }}
        >
          Criar Novo Estande
        </Button>
      </div>

      <Card style={{ background: colors.cardBackground }}>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Buscar por nome, vendedor ou ID"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </Space>
        <Table
          columns={columns}
          dataSource={filteredBooths}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: <Empty description="Nenhum estande encontrado" />,
          }}
        />
      </Card>

      {/* Create Booth Modal */}
      <Modal
        title={
          <span>
            <ShopOutlined style={{ marginRight: 8, color: colors.success }} />
            Criar Novo Estande
          </span>
        }
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewBoothName('');
        }}
        onOk={handleCreateBooth}
        confirmLoading={creating}
        okText="Criar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: colors.success } }}
      >
        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Nome do Estande:
          </label>
          <Input
            size="large"
            placeholder="Ex: Estande de Bebidas"
            value={newBoothName}
            onChange={(e) => setNewBoothName(e.target.value)}
            onPressEnter={handleCreateBooth}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Uma carteira será criada automaticamente para este estande.
          </Text>
        </div>
      </Modal>

      {/* Manage Vendors Modal */}
      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8, color: colors.info }} />
            Gerenciar Vendedores - {selectedBooth?.name}
          </span>
        }
        open={vendorModalVisible}
        onCancel={() => {
          setVendorModalVisible(false);
          setSelectedBooth(null);
        }}
        footer={[
          <Button key="close" onClick={() => setVendorModalVisible(false)}>
            Fechar
          </Button>,
        ]}
        width={600}
      >
        {selectedBooth && (
          <div>
            {/* Add vendor section */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Adicionar Vendedor:
              </label>
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Selecione um vendedor"
                  value={selectedVendorId || undefined}
                  onChange={setSelectedVendorId}
                  options={getAvailableVendors().map((v) => ({
                    value: v.id,
                    label: `${v.name} (${v.email})`,
                  }))}
                  notFoundContent={
                    <Empty
                      description="Nenhum vendedor disponível"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  }
                />
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={handleAddVendor}
                  loading={addingVendor}
                  disabled={!selectedVendorId}
                  style={{ background: colors.success }}
                >
                  Adicionar
                </Button>
              </Space.Compact>
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Apenas usuários com função "Vendedor" aparecem nesta lista.
              </Text>
            </div>

            {/* Current vendors list */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Vendedores Associados ({selectedBooth.vendorIds.length}):
              </label>
              {selectedBooth.vendorIds.length === 0 ? (
                <Empty
                  description="Nenhum vendedor associado a este estande"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedBooth.vendorIds.map((vendorId: string) => (
                    <div
                      key={vendorId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: colors.cardBackground,
                        borderRadius: 8,
                      }}
                    >
                      <Text>{getVendorName(vendorId)}</Text>
                      <Popconfirm
                        title="Remover vendedor"
                        description="Tem certeza que deseja remover este vendedor do estande?"
                        onConfirm={() => handleRemoveVendor(vendorId)}
                        okText="Sim"
                        cancelText="Não"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<UserDeleteOutlined />}
                          size="small"
                        >
                          Remover
                        </Button>
                      </Popconfirm>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Manage Schedule Modal */}
      <Modal
        title={
          <span>
            <ClockCircleOutlined style={{ marginRight: 8, color: colors.warning }} />
            Gerenciar Escalas - {selectedBooth?.name}
          </span>
        }
        open={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          setSelectedBooth(null);
          resetSlotForm();
        }}
        footer={[
          <Button key="close" onClick={() => {
            setScheduleModalVisible(false);
            resetSlotForm();
          }}>
            Fechar
          </Button>,
        ]}
        width={700}
      >
        {loadingSchedule ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <div>
            {/* Add/Edit slot form */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {editingSlot ? 'Editar Escala:' : 'Adicionar Escala:'}
              </label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <TimePicker
                  format="HH:mm"
                  placeholder="Início"
                  value={slotStartTime}
                  onChange={setSlotStartTime}
                  style={{ flex: 1 }}
                  minuteStep={5}
                />
                <TimePicker
                  format="HH:mm"
                  placeholder="Término"
                  value={slotEndTime}
                  onChange={setSlotEndTime}
                  style={{ flex: 1 }}
                  minuteStep={5}
                />
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%', marginBottom: 8 }}
                placeholder="Selecione os usuários"
                value={slotUserIds}
                onChange={setSlotUserIds}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                }
                options={allUsers.map((u) => ({
                  value: u.id,
                  label: `${u.name} (${u.email})`,
                }))}
                notFoundContent={
                  <Empty
                    description="Nenhum usuário encontrado"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                }
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="primary"
                  icon={editingSlot ? <EditOutlined /> : <PlusOutlined />}
                  onClick={handleSaveSlot}
                  loading={savingSlot}
                  disabled={!slotStartTime || !slotEndTime || slotUserIds.length === 0}
                  style={{ background: colors.success }}
                >
                  {editingSlot ? 'Salvar' : 'Adicionar'}
                </Button>
                {editingSlot && (
                  <Button onClick={resetSlotForm}>
                    Cancelar
                  </Button>
                )}
              </div>
            </div>

            {/* Schedule slots list */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Escalas do Dia ({scheduleSlots.length}):
              </label>
              {scheduleSlots.length === 0 ? (
                <Empty
                  description="Nenhuma escala cadastrada"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {scheduleSlots.map((slot) => (
                    <div
                      key={slot.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: colors.cardBackground,
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ marginRight: 12 }}>
                          {slot.startTime} - {slot.endTime}
                        </Text>
                        <Space wrap size={4}>
                          {slot.assignedUsers.map((u) => (
                            <Tag key={u.userId} color={colors.info}>
                              {u.userName}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                      <Space>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => handleEditSlot(slot)}
                        />
                        <Popconfirm
                          title="Excluir escala"
                          description="Tem certeza que deseja excluir esta escala?"
                          onConfirm={() => handleDeleteSlot(slot.id)}
                          okText="Sim"
                          cancelText="Não"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                          />
                        </Popconfirm>
                      </Space>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
}
